import React from 'react';
import { BookOpen, Award, Gamepad2, Coins, Undo2 } from 'lucide-react';

function fmtDate(s) {
    if (!s) return '—';
    try { return new Date(s).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }); } catch { return s; }
}
function fmtDateTime(s) {
    if (!s) return '—';
    try { return new Date(s).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch { return s; }
}

const REF_LABELS = {
    course_complete: 'Course completed', course: 'Course', quiz: 'Quiz', lesson: 'Lesson', game: 'Arcade game',
};

export function LearnerDetailView({ data }) {
    if (!data) return null;
    return (
        <div className="space-y-5" data-testid="learner-detail-view">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="learner-detail-summary">
                {[
                    { label: 'Total Points', value: data.summary.total_points },
                    { label: 'Courses Completed', value: data.summary.courses_completed },
                    { label: 'Certificates', value: data.summary.certificates },
                    { label: 'Games Played', value: data.summary.games_played },
                ].map((s) => (
                    <div key={s.label} className="rounded-2xl bg-slate-50 ring-1 ring-slate-200 p-3 text-center">
                        <div className="font-display font-extrabold text-2xl text-brand-blue">{s.value}</div>
                        <div className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mt-0.5">{s.label}</div>
                    </div>
                ))}
            </div>

            <Section icon={BookOpen} title={`Course history (${data.course_history.length})`}>
                {data.course_history.length === 0 ? <Empty text="No course activity yet." /> : (
                    <div className="overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                                <tr><th className="text-left py-1.5">Course</th><th className="text-left">Lang</th><th className="text-right">Progress</th><th className="text-right">Points</th><th className="text-right">Status</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.course_history.map((c) => (
                                    <tr key={c.course_id} data-testid={`learner-course-${c.course_id}`}>
                                        <td className="py-2 pr-2 font-semibold text-slate-800">{c.title}</td>
                                        <td className="uppercase text-slate-500 text-xs">{c.language}</td>
                                        <td className="text-right text-slate-600">{c.progress_percent}%</td>
                                        <td className="text-right font-bold text-brand-blue">{c.points_earned}</td>
                                        <td className="text-right">{c.completed ? <span className="text-emerald-600 font-bold text-xs">Completed</span> : <span className="text-amber-600 text-xs">In progress</span>}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            <Section icon={Award} title={`Certificates (${data.certificates.length})`}>
                {data.certificates.length === 0 ? <Empty text="No certificates earned yet." /> : (
                    <ul className="space-y-1.5">
                        {data.certificates.map((c) => (
                            <li key={c.certificate_id} className="flex items-center justify-between text-sm" data-testid={`learner-cert-${c.certificate_id}`}>
                                <span className="font-semibold text-slate-800">{c.course_title}</span>
                                <span className="text-slate-500 text-xs">{Math.round(c.score || 0)}% · {fmtDate(c.issued_at)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </Section>

            <Section icon={Gamepad2} title={`Games played (${data.games.length})`}>
                {data.games.length === 0 ? <Empty text="No games played yet." /> : (
                    <div className="overflow-auto">
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wide text-slate-500">
                                <tr><th className="text-left py-1.5">Game</th><th className="text-right">Plays</th><th className="text-right">Best score</th><th className="text-right">Points</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.games.map((g) => (
                                    <tr key={g.game_code} data-testid={`learner-game-${g.game_code}`}>
                                        <td className="py-2 font-semibold text-slate-800">{g.game_name}</td>
                                        <td className="text-right text-slate-600">{g.plays}</td>
                                        <td className="text-right text-slate-600">{g.best_score}</td>
                                        <td className="text-right font-bold text-brand-blue">{g.points_earned}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            <Section icon={Coins} title={`Points activity (${(data.points_timeline || []).length})`}>
                {(!data.points_timeline || data.points_timeline.length === 0) ? <Empty text="No points earned yet." /> : (
                    <div className="overflow-auto max-h-80">
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wide text-slate-500 sticky top-0 bg-white">
                                <tr><th className="text-left py-1.5">When</th><th className="text-left">Reason</th><th className="text-left">Type</th><th className="text-left">Txn ID</th><th className="text-right">Points</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.points_timeline.map((p, i) => (
                                    <tr key={i} data-testid={`learner-points-row-${i}`}>
                                        <td className="py-2 pr-2 text-slate-500 text-xs whitespace-nowrap">{fmtDateTime(p.created_at)}</td>
                                        <td className="pr-2 text-slate-800">{p.reason || '—'}</td>
                                        <td className="text-slate-500 text-xs">{REF_LABELS[p.ref_type] || p.ref_type || '—'}</td>
                                        <td className="pr-2 text-slate-500 text-[11px] font-mono whitespace-nowrap" data-testid={`learner-points-txn-${i}`}>{p.txn_id || '—'}</td>
                                        <td className="text-right font-bold text-emerald-600">+{p.points}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Section>

            {(data.rollbacks || []).length > 0 && (
                <Section icon={Undo2} title={`Points rolled back (${data.rollbacks.length})`}>
                    <div className="rounded-xl bg-amber-50 ring-1 ring-amber-200 px-3 py-2 mb-3 text-xs text-amber-800">
                        Legacy <b>video-only</b> completion points deducted (awarded before the quiz-pass rule).
                        Total removed: <b>−{data.summary?.points_rolled_back ?? data.rollbacks.reduce((a, r) => a + (r.points || 0), 0)}</b> pts.
                    </div>
                    <div className="overflow-auto max-h-72">
                        <table className="w-full text-sm">
                            <thead className="text-[11px] uppercase tracking-wide text-slate-500 sticky top-0 bg-white">
                                <tr><th className="text-left py-1.5">Course</th><th className="text-left">Transaction ID</th><th className="text-left">Awarded</th><th className="text-left">Rolled back</th><th className="text-right">Points</th></tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {data.rollbacks.map((r, i) => (
                                    <tr key={i} data-testid={`learner-rollback-row-${i}`}>
                                        <td className="py-2 pr-2 font-semibold text-slate-800">{r.course_title || '—'}</td>
                                        <td className="pr-2 text-slate-500 text-[11px] font-mono whitespace-nowrap" data-testid={`learner-rollback-txn-${i}`}>{r.transaction_id || '—'}</td>
                                        <td className="text-slate-500 text-xs whitespace-nowrap">{fmtDate(r.awarded_at)}</td>
                                        <td className="text-slate-500 text-xs whitespace-nowrap">{fmtDate(r.rolled_back_at)}</td>
                                        <td className="text-right font-bold text-rose-600">−{r.points}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </Section>
            )}
        </div>
    );
}

function Section({ icon: Icon, title, children }) {
    return (
        <div className="rounded-2xl ring-1 ring-slate-200 p-4 bg-white">
            <div className="flex items-center gap-2 text-slate-800 font-bold mb-3"><Icon className="h-4 w-4 text-brand-blue" /> {title}</div>
            {children}
        </div>
    );
}

function Empty({ text }) {
    return <div className="text-sm text-slate-400 py-2">{text}</div>;
}
